import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Alert, Button, Checkbox, Modal, Progress, Space, Typography } from "antd";
import JSZip from "jszip";
import { AdmsAsset, DcatCatalogs, ModelViews, RdfTriplet, SubjectRepository } from "../../redux";
import Model from "../../views/models/model";
import { lastElementCardURI } from "../../utils/uriUtil";
import { collectGraphsFromSPARQL } from "./exportGraph";

const { Link, Text, Title } = Typography;
const P = {
  dataset: "http://www.w3.org/ns/dcat#dataset",
  scheme: "http://www.w3.org/ns/dcat#themeTaxonomy",
  theme: "http://www.w3.org/ns/dcat#theme",
  distribution: "http://www.w3.org/ns/dcat#distribution",
  type: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
  inScheme: "http://www.w3.org/2004/02/skos/core#inScheme",
  topConceptOf: "http://www.w3.org/2004/02/skos/core#topConceptOf",
};
const OPT = { WIKI: "wiki", DIST: "distribution", CLASS: "classification", RELATED: "relatedRefs", VIEWS: "views", GRAPHS: "graphs", ALL: "wholeGraph" };
const LABEL = {
  [OPT.WIKI]: "Включить информацию о wiki-статьях",
  [OPT.DIST]: "Включить информацию о распространении актива",
  [OPT.CLASS]: "Включить детальную информацию о классификационных признаках и концептах",
  [OPT.RELATED]: "Включить детальную информацию о связанных элементах",
  [OPT.VIEWS]: "Включить визуальные представления",
  [OPT.GRAPHS]: "Выгрузить графы, указанные в представлениях",
  [OPT.ALL]: "Весь граф",
};
const DEFAULTS = [OPT.WIKI, OPT.DIST, OPT.VIEWS];

function ExportCatalog({ open, onClose, dispatch, graphURI, catalogURI }) {
  const [checked, setChecked] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [zipUrl, setZipUrl] = useState("");
  const urlRef = useRef("");
  const done = Boolean(zipUrl);
  const zipName = useMemo(() => {
    const stamp = new Date().toISOString().replaceAll(":", "-").replaceAll(".", "-");
    return `export_catalog_${lastElementCardURI(catalogURI || "catalog")}_${stamp}.zip`;
  }, [catalogURI]);

  useEffect(() => { urlRef.current = zipUrl; }, [zipUrl]);
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);
  useEffect(() => {
    if (!open) return;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = "";
    setZipUrl(""); setChecked(DEFAULTS); setBusy(false); setProgress(0); setStatus(""); setError(null);
  }, [open]);

  async function addViews(zip, manifest, resource, root, model, selected, exportedGraphs) {
    if (!selected.has(OPT.VIEWS)) return;
    const views = (await model.getViews(resource))?.list || [];
    for (const meta of views) {
      const data = await model.getView(meta.id);
      const file = `${root}/views/${safe(meta.id)}.json`;
      zip.file(file, `${JSON.stringify(data)}\n`);
      manifest.push({ view: { resource, id: meta.id }, file });
      if (!selected.has(OPT.GRAPHS)) continue;
      for (const graph of graphIRIs(data?.view)) {
        if (exportedGraphs.has(graph)) continue;
        const state = await AdmsAsset.checkGraphState2(graph);
        if (state.state !== "present") continue;
        exportedGraphs.add(graph);
        const graphFile = `graphs/${safe(graph)}.ttl`;
        zip.file(graphFile, `${await AdmsAsset.exportGraph(graph)}\n`);
        manifest.push({ graph: { resource, graph }, file: graphFile });
      }
    }
  }

  async function addDataset(zip, manifest, asset, index, model, selected, exportedGraphs) {
    const triples = (await RdfTriplet.loadSubject(asset, graphURI)).bindings || [];
    const iris = [asset];
    if (selected.has(OPT.WIKI)) append(triples, iris, predicate => /wiki/i.test(predicate));
    if (selected.has(OPT.DIST)) append(triples, iris, predicate => predicate === P.distribution);
    if (selected.has(OPT.CLASS)) append(triples, iris, predicate => predicate === P.theme);
    if (selected.has(OPT.RELATED)) append(triples, iris, predicate => predicate !== P.type);
    const root = `datasets/${index + 1}_${safe(asset)}`;
    const file = `${root}/card_asset.ttl`;
    const exportIRIs = unique(iris);
    zip.file(file, `${await AdmsAsset.exportCard(graphURI, exportIRIs)}\n`);
    manifest.push({ card: { asset, IRIs: exportIRIs }, file });
    await addViews(zip, manifest, asset, root, model, selected, exportedGraphs);
  }

  async function addScheme(zip, manifest, scheme, index, selected) {
    const iris = [scheme];
    if (selected.has(OPT.RELATED)) {
      const triples = (await RdfTriplet.loadSubject(scheme, graphURI)).bindings || [];
      append(triples, iris, predicate => predicate !== P.type && predicate !== P.topConceptOf);
    }
    if (selected.has(OPT.CLASS)) {
      const triples = (await RdfTriplet.loadObject(scheme, graphURI)).bindings || [];
      triples.filter(item => item.predicat?.value === P.inScheme).forEach(item => iris.push(item.subject?.value));
    }
    const exportIRIs = unique(iris);
    const file = `schemes/${index + 1}_${safe(scheme)}.ttl`;
    zip.file(file, `${await SubjectRepository.exportSubject(graphURI, exportIRIs)}\n`);
    manifest.push({ scheme: { scheme, IRIs: exportIRIs }, file });
  }

  async function handleExport() {
    setBusy(true); setError(null); setProgress(0); setZipUrl("");
    try {
      const selected = new Set(checked);
      const zip = new JSZip();
      const manifest = [];
      const exportedGraphs = new Set();
      const model = new Model(ModelViews.getRpc_url());
      setStatus("Получение содержимого каталога…");
      const response = await dispatch(DcatCatalogs.loadCatalogDetails(catalogURI, graphURI));
      const triples = response?.results?.bindings || [];
      const datasets = objects(triples, P.dataset);
      const schemes = objects(triples, P.scheme);
      zip.file("catalog.ttl", `${await SubjectRepository.exportSubject(graphURI, [catalogURI])}\n`);
      manifest.push({ catalog: { catalog: catalogURI }, file: "catalog.ttl" });

      if (selected.has(OPT.ALL)) {
        setStatus("Экспорт всего графа…");
        zip.file("graph.ttl", `${await AdmsAsset.exportGraph(graphURI)}\n`);
        manifest.push({ graph: { graph: graphURI, whole: true }, file: "graph.ttl" });
      }

      for (let i = 0; i < datasets.length; i += 1) {
        setStatus(`Экспорт набора данных ${i + 1} из ${datasets.length}…`);
        await addDataset(zip, manifest, datasets[i], i, model, selected, exportedGraphs);
        setProgress(Math.round(((i + 1) / Math.max(datasets.length + schemes.length, 1)) * 70));
      }
      for (let i = 0; i < schemes.length; i += 1) {
        setStatus(`Экспорт справочника ${i + 1} из ${schemes.length}…`);
        await addScheme(zip, manifest, schemes[i], i, selected);
      }

      if (selected.has(OPT.ALL) && selected.has(OPT.VIEWS)) {
        const resources = unique(await dispatch(DcatCatalogs.loadResourceIRIs(graphURI)));
        for (let i = 0; i < resources.length; i += 1) {
          if (datasets.includes(resources[i])) continue;
          setStatus(`Экспорт представлений ресурса ${i + 1} из ${resources.length}…`);
          await addViews(zip, manifest, resources[i], `resources/${i + 1}_${safe(resources[i])}`, model, selected, exportedGraphs);
        }
      }

      zip.file("export.json", JSON.stringify(manifest, null, 2));
      zip.file("export.log", `Catalog: ${catalogURI}\nGraph: ${graphURI}\nGenerated: ${new Date().toISOString()}\n`);
      setStatus("Упаковка ZIP…");
      const blob = await zip.generateAsync({ type: "blob" }, meta => setProgress(70 + Math.round(meta.percent * 0.3)));
      const url = URL.createObjectURL(blob);
      urlRef.current = url; setZipUrl(url); setProgress(100); setStatus("Готово.");
      download(url, zipName);
    } catch (e) {
      setError(e?.message || String(e)); setProgress(0); setStatus("");
    } finally { setBusy(false); }
  }

  function close() {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = ""; setZipUrl(""); onClose?.();
  }

  return <Modal title={<Title level={5} style={{ margin: 0 }}>Экспорт каталога</Title>} open={open} onCancel={close}
    maskClosable={!busy} closable={!busy} footer={<Space>
      {!done && <Button onClick={close} disabled={busy}>Отмена</Button>}
      <Button type="primary" loading={busy} disabled={busy || !catalogURI || !graphURI} onClick={done ? close : handleExport}>{done ? "Закрыть" : "Экспорт"}</Button>
    </Space>}>
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Text type="secondary">Выберите разделы, которые нужно включить в экспорт:</Text>
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        {[OPT.WIKI, OPT.DIST, OPT.CLASS, OPT.RELATED, OPT.VIEWS].map(option =>
          <Checkbox key={option} checked={checked.includes(option)} disabled={busy || done} onChange={e => toggle(option, e.target.checked, checked, setChecked)}>{LABEL[option]}</Checkbox>)}
        <Checkbox checked={checked.includes(OPT.GRAPHS)} disabled={busy || done || !checked.includes(OPT.VIEWS)} style={{ marginLeft: 24 }} onChange={e => toggle(OPT.GRAPHS, e.target.checked, checked, setChecked)}>{LABEL[OPT.GRAPHS]}</Checkbox>
        <Checkbox checked={checked.includes(OPT.ALL)} disabled={busy || done} onChange={e => toggle(OPT.ALL, e.target.checked, checked, setChecked)}>{LABEL[OPT.ALL]}</Checkbox>
      </Space>
      {(busy || done) && <><Progress percent={progress}/>{status && <Text>{status}</Text>}</>}
      {error && <Alert type="error" showIcon message="Ошибка экспорта" description={error}/>} 
      {done && <Space orientation="vertical"><Text strong>Ссылка на скачивание:</Text><Link href={zipUrl} download={zipName}>Скачать {zipName}</Link></Space>}
    </Space>
  </Modal>;
}

function objects(triples, predicate) { return unique((triples || []).filter(x => x.predicat?.value === predicate && x.object?.type === "uri").map(x => x.object.value)); }
function append(triples, target, filter) { (triples || []).filter(x => x.object?.type === "uri" && filter(x.predicat?.value, x)).forEach(x => target.push(x.object.value)); }
function unique(values) { return [...new Set((values || []).filter(Boolean))]; }
function safe(value) { return encodeURIComponent(String(value || "item")).replace(/%/g, "_"); }
function toggle(option, enabled, checked, setter) { const next = new Set(checked); enabled ? next.add(option) : next.delete(option); if (option === OPT.VIEWS && !enabled) next.delete(OPT.GRAPHS); setter([...next]); }
function graphIRIs(view) {
  if (!view || typeof view !== "object") return [];
  const query = view.type === "graph" ? view.sparql : view.src_query;
  let result = [];
  if (query) { try { result = collectGraphsFromSPARQL(query); } catch (e) { result = []; } }
  if (view.type === "graph" && view.graph) result.push(view.graph);
  return unique(result);
}
function download(url, filename) { const a = document.createElement("a"); a.href = url; a.download = filename; a.style.display = "none"; document.body.appendChild(a); a.click(); a.remove(); }
const mapStateToProps = (state, props) => ({ ...props, graphURI: props.graphURI ?? props.graph ?? state.vset?.graph?.signed, catalogURI: props.catalogURI ?? props.catalog });
export default connect(mapStateToProps)(ExportCatalog);
